import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin, Search } from "lucide-react";
import { loadGoogleMaps, parseAddressComponents } from "@/lib/googleMaps";
import { logger } from "@/lib/logger";

export { loadGoogleMaps };

/**
 * PlacesAutocomplete — search box backed by Google Places API (New).
 * Suggestions are restricted to India and biased towards `near` ({lat,lng}).
 * On selection calls onPlaceSelected({formatted_address, address, name, area,
 * city, state, pincode, lat, lng, place_id}). Without a Maps key it is a plain
 * input (onPlaceSelected is never called). With onPincode, typing a 6-digit
 * pincode offers "Use pincode …" (Enter picks it), with or without Google.
 */
const PIN = /^[1-9]\d{5}$/;

export function PlacesAutocomplete({ value, onChange, onPlaceSelected, onPincode, placeholder, dataTestid, className, near }) {
  const [places, setPlaces] = useState(null);      // google.maps.places library
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [failed, setFailed] = useState(false);   // Google search couldn't be used
  const token = useRef(null);
  const typed = useRef(false);

  useEffect(() => {
    let alive = true;
    loadGoogleMaps()
      .then(async (g) => {
        if (!alive) return;
        if (!g) { setFailed(true); return; }
        const lib = await g.maps.importLibrary("places");
        if (alive) setPlaces(lib);
      })
      .catch((e) => { logger.warn("Places library unavailable:", e); if (alive) setFailed(true); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // Fetch suggestions as the customer types (debounced).
  useEffect(() => {
    if (!places || !typed.current) return;
    const input = (value || "").trim();
    if (input.length < 3) { setSuggestions([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        if (!token.current) token.current = new places.AutocompleteSessionToken();
        const req = { input, sessionToken: token.current, includedRegionCodes: ["in"], language: "en" };
        if (near?.lat != null) req.locationBias = { center: { lat: near.lat, lng: near.lng }, radius: 30000 };
        const { suggestions: rows } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
        if (!alive) return;
        setSuggestions((rows || []).map((s) => s.placePrediction).filter(Boolean).slice(0, 6));
        setOpen(true);
        setActive(-1);
        setFailed(false);
      } catch (e) {
        logger.warn("Place suggestions failed:", e);
        setFailed(true);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [value, places, near?.lat, near?.lng]);

  const pick = async (prediction) => {
    setOpen(false);
    setSuggestions([]);
    typed.current = false;
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["displayName", "formattedAddress", "location", "addressComponents"] });
      token.current = null;   // a selection ends the billing session
      const parsed = parseAddressComponents(place.addressComponents || []);
      const payload = {
        formatted_address: place.formattedAddress,
        address: place.formattedAddress,
        name: place.displayName || "",
        ...parsed,
        lat: place.location.lat(),
        lng: place.location.lng(),
        place_id: place.id,
      };
      setTyping(false);
      onChange && onChange(place.formattedAddress || prediction.text?.text || "");
      onPlaceSelected && onPlaceSelected(payload);
    } catch (e) {
      logger.warn("Place details failed:", e);
    }
  };

  const [typing, setTyping] = useState(false);
  const pin = onPincode && typing && PIN.test((value || "").trim()) ? (value || "").trim() : null;
  const pickPin = () => { setOpen(false); setSuggestions([]); typed.current = false; setTyping(false); onPincode(pin); };

  const onKeyDown = (e) => {
    if (pin && e.key === "Enter" && active < 0) { e.preventDefault(); pickPin(); return; }
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
      <Input
        value={value || ""}
        onChange={(e) => { typed.current = true; setTyping(true); onChange && onChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || "Search for your building, street or landmark…"}
        data-testid={dataTestid}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className={className || "h-11 pl-9 pr-8"}
      />
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
      ) : places ? (
        <MapPin className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-teal" />
      ) : null}
      </div>
      {pin && (
        <button type="button" onMouseDown={(e) => { e.preventDefault(); pickPin(); }} data-testid={dataTestid ? `${dataTestid}-pincode` : undefined}
          className="mt-1 w-full text-left rounded-xl border border-teal/40 bg-teal/5 px-3 py-2 text-sm font-semibold text-teal hover:bg-teal/10">
          Use pincode {pin}
        </button>
      )}
      {!pin && failed && typed.current && (value || "").trim().length >= 3 && (
        <p className="mt-1 text-xs text-terracotta" data-testid={dataTestid ? `${dataTestid}-unavailable` : undefined}>
          Address search isn't available right now. Type your 6-digit pincode, use your current location or move the map pin instead.
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul role="listbox" className="absolute z-50 mt-1 w-full rounded-xl border border-stone-200 bg-white shadow-lg overflow-hidden" data-testid={dataTestid ? `${dataTestid}-suggestions` : undefined}>
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className={`px-3 py-2 cursor-pointer text-sm flex gap-2 items-start ${i === active ? "bg-stone-100" : "hover:bg-stone-50"}`}
            >
              <MapPin className="h-4 w-4 mt-0.5 text-stone-400 shrink-0" />
              <span className="min-w-0">
                <span className="block font-medium text-stoneWarm truncate">{s.mainText?.text || s.text?.text}</span>
                {s.secondaryText?.text && <span className="block text-xs text-stone-500 truncate">{s.secondaryText.text}</span>}
              </span>
            </li>
          ))}
          <li className="px-3 py-1 text-[10px] text-stone-400 text-right">powered by Google</li>
        </ul>
      )}
    </div>
  );
}
